/** Sentinel monitoring watchlist, every case being watched rather than investigated. */
import { useState, useEffect } from 'react';
import { api, Spinner, Badge, Hint, dateShort, PRIORITY_TONE } from './ui';

const daysUntil = (d) => (d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null);

export default function SentinelWatchlist({ onOpenCase }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let on = true;
    api('/queue').then((j) => on && setItems(j.queue?.monitoring || [])).catch((e) => on && setErr(e.message)).finally(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  if (loading) return <Spinner label="Loading the watchlist…" />;
  if (err) return <div className="p-6 text-sm text-red-600">{err}</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center mb-3">
          <h2 className="font-semibold text-slate-800">Monitoring watchlist</h2>
          <Hint text="Cases you chose to watch rather than investigate. Each shows what you are watching for and when it is due for review. Overdue reviews are flagged in red." />
          <span className="ml-auto text-xs font-semibold text-slate-400">{items.length}</span>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Nothing on the watchlist. When you choose Monitor on a referral, it appears here.</p>
        ) : (
          <div className="space-y-2">
            {items.map((c) => {
              const d = daysUntil(c.monitor_review_date);
              const overdue = d != null && d < 0;
              const soon = d != null && d >= 0 && d <= 7;
              return (
                <button key={c.id} onClick={() => onOpenCase(c.id)} className="w-full text-left flex items-start gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors">
                  <span className="font-mono text-xs text-slate-400 mt-0.5">{c.case_number}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-700 truncate">{c.allegation_summary || '(no summary)'}</div>
                    {c.monitor_reason && <div className="text-xs text-slate-400 truncate">Watching for: {c.monitor_reason}</div>}
                  </div>
                  {c.priority && <Badge tone={PRIORITY_TONE[c.priority] || 'slate'}>{c.priority}</Badge>}
                  {c.monitor_review_date
                    ? <Badge tone={overdue ? 'red' : soon ? 'amber' : 'slate'}>{overdue ? `review ${Math.abs(d)}d overdue` : `review ${dateShort(c.monitor_review_date)}`}</Badge>
                    : <Badge tone="slate">no review date</Badge>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
